import React, { useMemo, useRef } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import useComponentSize from "@rehooks/component-size";
import DrawTemplate from "../../../components/Template/DrawTemplate";
import { Skeleton } from "@material-ui/lab";
import { getBoxSize } from "../../../utils";
import { createInstance } from "../../../Providers/TemplateInstanceProvider";
import { useAPI } from "../../../lib/common";
import Box from "@material-ui/core/Box";
import useRoutes, { getLinkComponentAttributes } from "../../hooks/useRoutes";
import useIsVisible from "../../../hooks/useIsVisible";

const useStyles = makeStyles((theme) => ({
  templateList: {
    margin: 0,
    padding: 0,
    display: "grid",
    [theme.breakpoints.up("xs")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    [theme.breakpoints.up("lg")]: {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
    gridGap: theme.spacing(1),
    listStyle: "none",
    "& > li": {
      position: "relative",
      backgroundColor: theme.palette.primary.light,
      boxShadow: "0px 3px 6px #00000029",
      [theme.breakpoints.down("xs")]: {
        //flexBasis: '50%'
      },
    },
  },
  contentContainer: {
    //display: 'block',
    boxSizing: "border-box",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    textDecoration: "none",
    "&:hover $templatePreviewContainer": {
      transform: "scale(1.02)",
    },
  },
  aspectRatioOuterContainer: {
    width: "100%",
    paddingTop: "calc(400 / 290 * 100%)",
    position: "relative",
  },
  aspectRatioInnerContainer: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  templatePreviewContainer: {
    flexGrow: 1,
    padding: "10px",
    boxSizing: "border-box",
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: "transform 0.2s",
    "& .template-inner-container": {
      boxShadow: "0px 3px 6px #00000029",
    },
    "&.no-shadow .template-inner-container, &.no-shadow $previewImage": {
      boxShadow: "none",
    },
  },
  templateContainer: {
    flexGrow: 1,
    padding: "10px",
    boxSizing: "border-box",
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& .template-inner-container": {
      boxShadow: "0px 3px 6px #00000029",
    },
    "&.no-shadow .template-inner-container, &.no-shadow $previewImage": {
      boxShadow: "none",
    },
  },
  templateName: {
    backgroundColor: "white",
    height: "50px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: 300,
    textTransform: "none",
    color: "black",
    fontSize: "12px",
    textAlign: "center",
  },
  photgiftPreviewContainer: {
    backgroundColor: "white",
  },
  skeletonItem: {
    width: "100%",
    height: "100%",
  },
  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center center",
  },
}));

export default ({
  product,
  templates,
  pictureSet,
  displayTemplateName,
  type,
  category,
  canViewWithOwnPictures,
  separateFromBackground,
}) => {
  const classes = useStyles();
  const routes = useRoutes();

  const previewOwnImages =
    pictureSet.pictures.length > 0 && canViewWithOwnPictures;

  console.log("The category is", category);

  return (
    <ul className={classes.templateList}>
      {templates.map((template) => {
        let path = "products.categories.category.create";
        if (type == "calendars") {
          path = "products.calendars.create";
        }
        const link = getLinkComponentAttributes(
          {
            path,
            variables: {
              type: type,
              category: category,
              productUuid: template.product.uuid,
              templateUuid: template.uuid,
            },
          },
          routes,
        );

        return (
          <li key={`${template.uuid}-${template.product.uuid}`}>
            <Link {...link} className={classes.contentContainer}>
              <div className={classes.aspectRatioOuterContainer}>
                <div className={classes.aspectRatioInnerContainer}>
                  {previewOwnImages ? (
                    <TemplateContainer
                      product={product}
                      template={template}
                      pictureSet={pictureSet}
                      separateFromBackground={separateFromBackground}
                    />
                  ) : (
                    <TemplatePreviewContainer
                      template={template}
                      separateFromBackground={separateFromBackground}
                    />
                  )}
                </div>
              </div>
              {displayTemplateName && (
                <Box className={classes.templateName}>{template.name}</Box>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

const Link = ({ component: Component, ...props }) => {
  return <Component {...props} />;
};

const TemplatePreviewContainer = ({ template, separateFromBackground }) => {
  // Use a ref to attach to the element whose visibility you want to keep track of.
  const classes = useStyles();
  console.log("Template preview image", template.uuid);
  return (
    <div
      className={`${classes.templatePreviewContainer} ${
        separateFromBackground ? "" : "no-shadow"
      }`}
    >
      <img
        className={`${classes.previewImage}`}
        src={`/templates/all/${template.preview.large}`}
      />
    </div>
  );
};

const TemplateContainer = ({
  template,
  pictureSet,
  separateFromBackground,
}) => {
  // Use a ref to attach to the element whose visibility you want to keep track of.
  const ref = useRef();
  const classes = useStyles();
  // `current` points to the mounted img element.
  const isVisible = useIsVisible(ref);
  const size = useComponentSize(ref);
  const cs = useMemo(() => {
    if (ref.current) {
      const padding = parseInt(
        window
          .getComputedStyle(ref.current, null)
          .getPropertyValue("padding-left"),
      );
      const width = size.width - padding * 2;
      const height = size.height - padding * 2;

      const cSize = {
        width: width * 0.8,
        height: height * 0.8,
      };

      let visibleSize = {
        width: template.width,
        height: template.height,
      };
      if (template.trimBox) {
        visibleSize = {
          width: template.trimBox.width,
          height: template.trimBox.height,
        };
      }
      console.log("Template container template", template);

      const info = getBoxSize("fit-in", visibleSize, cSize);
      console.log("Template area is", size, info);
      return info;
    }
    return size;
  }, [size]);

  return (
    <div
      ref={ref}
      className={`${classes.templateContainer} ${
        separateFromBackground ? "" : "no-shadow"
      }`}
    >
      <div
        style={{
          width: cs.width + "px",
          height: cs.height + "px",
          position: "relative",
        }}
      >
        {isVisible ? (
          <TemplateInnerContainer
            size={cs}
            templateListItem={template}
            pictureSet={pictureSet}
          />
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

const TemplateInnerContainer = ({ size, templateListItem, pictureSet }) => {
  const [template, loading] = useAPI(
    "/api/webshop/templates/by-uuid/" +
      templateListItem.uuid +
      "/" +
      templateListItem.version,
  );
  const templateInstance = useMemo(() => {
    if (loading) {
      return null;
    }
    let instance = createInstance(template, templateListItem.userResources);
    console.log("The instance is ", instance);
    return instance;
  }, [template, pictureSet, loading]);

  if (!templateInstance) {
    return null;
  }

  return (
    <DrawTemplate containerSize={size} templateInstance={templateInstance} />
  );
};

export const TemplateListItemsSkeleton = ({ displayTemplateName }) => {
  const classes = useStyles();

  const numProducts = Array.from(Array(10).keys());

  return (
    <ul className={classes.templateList}>
      {numProducts.map((i) => {
        return (
          <li key={i}>
            <div className={classes.aspectRatioOuterContainer}>
              <div className={classes.aspectRatioInnerContainer}>
                <Skeleton variant="rect" className={classes.skeletonItem} />
              </div>
            </div>
            {displayTemplateName && (
              <Box className={classes.templateName}>
                <Skeleton variant="text" width="140" />
              </Box>
            )}
          </li>
        );
      })}
    </ul>
  );
};
